import { supabase } from "@/integrations/supabase/client";
import { createAppointmentWithOrder, type AppointmentInput } from "@/hooks/useAppointments";
import { shouldBlockRemindersForPet } from "@/lib/utils/pet-death";

const DEFAULT_APPOINTMENT_DURATION_MINUTES = 60;

/**
 * Cria ou atualiza um agendamento de retorno baseado na data de retorno do prontuário.
 * 
 * @param params - Parâmetros para criar/atualizar o agendamento
 * @returns ID do agendamento criado/atualizado ou null se não foi possível criar
 */
export async function createOrUpdateFollowUpAppointment({
  medicalRecordId,
  petId,
  ownerId,
  clinicId,
  vetUserId,
  followUpDate,
  followUpNotes,
  existingAppointmentId,
}: {
  medicalRecordId: string;
  petId: string;
  ownerId: string;
  clinicId: string;
  vetUserId: string | null;
  followUpDate: Date | null;
  followUpNotes: string | null;
  existingAppointmentId?: string | null;
}): Promise<string | null> {
  try {
    // Se não há data de retorno, não criar/atualizar agendamento
    if (!followUpDate) {
      // Se havia um agendamento existente, podemos cancelá-lo (opcional)
      // Por enquanto, apenas retornamos null
      return null;
    }

    const { data: pet } = await supabase
      .from("pets")
      .select("death_date, death_cause, status:pet_statuses(name)")
      .eq("id", petId)
      .single();

    if (shouldBlockRemindersForPet(pet)) {
      return null;
    }

    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error("Usuário não autenticado");
    }

    const followUpStart = followUpDate instanceof Date ? followUpDate : new Date(followUpDate);
    const followUpEnd = new Date(
      followUpStart.getTime() + DEFAULT_APPOINTMENT_DURATION_MINUTES * 60 * 1000,
    );

    // Verificar se já existe um agendamento de retorno para este pet na mesma data/hora
    // ou usar o existingAppointmentId se fornecido
    let appointmentToUpdate: string | null = existingAppointmentId || null;

    const { data: retornoType } = await supabase
      .from("clinic_appointment_types")
      .select("id")
      .eq("clinic_id", clinicId)
      .eq("behavior", "retorno")
      .maybeSingle();

    if (!appointmentToUpdate && retornoType) {
      // Buscar agendamento existente do tipo "retorno" para o mesmo pet na mesma data/hora
      // Usar um range de tempo (5 minutos antes e depois) para evitar problemas com milissegundos
      const timeWindowStart = new Date(followUpStart.getTime() - 5 * 60 * 1000);
      const timeWindowEnd = new Date(followUpStart.getTime() + 5 * 60 * 1000);

      const { data: existingAppointments, error: searchError } = await supabase
        .from("appointments")
        .select("id")
        .eq("clinic_id", clinicId)
        .eq("owner_id", ownerId)
        .eq("type", retornoType.id)
        .gte("starts_at", timeWindowStart.toISOString())
        .lte("starts_at", timeWindowEnd.toISOString())
        .in("status", ["agendado", "confirmado"])
        .is("deleted_at", null);

      if (searchError) {
        console.warn("Erro ao buscar agendamento existente:", searchError);
      } else if (existingAppointments && existingAppointments.length > 0) {
        // Verificar se algum dos agendamentos tem o mesmo pet
        for (const appointment of existingAppointments) {
          const { data: appointmentPets } = await supabase
            .from("appointment_pets")
            .select("pet_id")
            .eq("appointment_id", appointment.id);

          if (appointmentPets?.some((ap) => ap.pet_id === petId)) {
            appointmentToUpdate = appointment.id;
            break;
          }
        }
      }
    }

    // Construir notas do agendamento
    const notes = followUpNotes?.trim() || null;

    if (appointmentToUpdate) {
      // Atualizar agendamento existente
      const { data: appointment, error: updateError } = await supabase
        .from("appointments")
        .update({
          starts_at: followUpStart.toISOString(),
          ends_at: followUpEnd.toISOString(),
          notes,
          vet_user_id: vetUserId,
        })
        .eq("id", appointmentToUpdate)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      return appointment.id;
    } else {
      if (!retornoType) {
        console.warn('Tipo de agendamento "retorno" não configurado para esta clínica');
        return null;
      }

      // Criar novo agendamento
      const appointmentData: AppointmentInput = {
        clinic_id: clinicId,
        vet_user_id: vetUserId,
        owner_id: ownerId,
        pet_ids: [petId],
        type: retornoType.id,
        starts_at: followUpStart.toISOString(),
        ends_at: followUpEnd.toISOString(),
        notes,
        status: "agendado",
      };

      const appointment = await createAppointmentWithOrder(appointmentData);
      return appointment.id;
    }
  } catch (error) {
    console.error("Erro ao criar/atualizar agendamento de retorno:", error);
    // Não lançar erro para não impedir o salvamento do prontuário
    return null;
  }
}
